import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReCaptcha from "react-google-recaptcha";
import Card from "@mui/material/Card";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import BasicLayout from "layouts/authentication/components/BasicLayout";

import { API_BASE } from "config/api";
import { useLanguage } from "context/LanguageContext";
import { normalizeUserUiLanguage } from "constants/uiLocales";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const ENABLE_RECAPTCHA = process.env.REACT_APP_ENABLE_RECAPTCHA !== "false";

function Basic() {
  const [email_id, setEmailId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [autoFilled, setAutoFilled] = useState({ email: false, password: false }); // Track autofill

  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "success",
    title: "",
    message: "",
  });

  const recaptchaRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const navigate = useNavigate();
  const { setLanguage } = useLanguage();

  /* -------------------- AUTH CHECK -------------------- */
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = localStorage.getItem("user");
        const authToken = localStorage.getItem("auth_token");

        if (userData && authToken) {
          navigate("/dashboard");
        } else {
          localStorage.removeItem("user");
          localStorage.removeItem("user_id");
          localStorage.removeItem("auth_token");
        }
      } catch (error) {
        console.error("Error checking auth:", error);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, [navigate]);

  /* -------------------- DETECT AUTOFILL -------------------- */
  useEffect(() => {
    // Function to check if inputs have autofilled value
    const checkAutofill = () => {
      if (emailRef.current) {
        const emailInput = emailRef.current.querySelector("input");
        if ((emailInput && emailInput.matches(":autofill")) || emailInput?.value) {
          setEmailId(emailInput.value || "");
          setAutoFilled((prev) => ({ ...prev, email: true }));
        }
      }

      if (passwordRef.current) {
        const passwordInput = passwordRef.current.querySelector("input");
        if ((passwordInput && passwordInput.matches(":autofill")) || passwordInput?.value) {
          setPassword(passwordInput.value || "");
          setAutoFilled((prev) => ({ ...prev, password: true }));
        }
      }
    };

    // Check after a delay to allow browser to autofill
    const timer = setTimeout(checkAutofill, 100);

    // Also listen for animation start which browsers trigger on autofill
    const handleAnimationStart = (e) => {
      if (e.animationName === "onAutoFillStart") {
        setAutoFilled((prev) => ({
          ...prev,
          [e.target.name === "email" ? "email" : "password"]: true,
        }));
      }
    };

    document.addEventListener("animationstart", handleAnimationStart);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("animationstart", handleAnimationStart);
    };
  }, []);

  /* -------------------- AUTOFOCUS -------------------- */
  useEffect(() => {
    if (!checkingAuth && emailRef.current) {
      const input = emailRef.current.querySelector("input");
      if (input) {
        input.focus();
        const length = input.value?.length || 0;
        if (length > 0) {
          input.setSelectionRange(length, length);
        }
      }
    }
  }, [checkingAuth]);

  /* -------------------- SNACKBAR -------------------- */
  const showSnackbar = ({ color, title, message, duration = 3000 }) => {
    setSnackbar({ open: true, color, title, message });
    setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), duration);
  };

  const closeSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  /* -------------------- RECAPTCHA -------------------- */
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setRecaptchaError(false);
  };

  const resetRecaptcha = () => {
    setRecaptchaToken("");
    recaptchaRef.current?.reset();
  };

  /* -------------------- LOGIN -------------------- */
  async function login() {
    if (!email_id || !password) {
      showSnackbar({
        color: "error",
        title: "Missing fields",
        message: "Please enter email and password",
      });
      return;
    }

    if (ENABLE_RECAPTCHA && !recaptchaToken) {
      setRecaptchaError(true);
      showSnackbar({
        color: "error",
        title: "Verification required",
        message: "Please complete the reCAPTCHA verification",
      });
      return;
    }

    setLoading(true);

    try {
      const url = new URL("api/auth/login", API_BASE);

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_id,
          password,
          ...(ENABLE_RECAPTCHA && { recaptchaToken }),
        }),
      });

      const result = await res.json();

      if (result.status === "success" && result.data) {
        const userData = result.data;
        const nextLang = normalizeUserUiLanguage(userData.language || "en");

        localStorage.setItem("user", JSON.stringify({ ...userData, language: nextLang }));
        localStorage.setItem("user_id", userData.id);
        localStorage.setItem("selectedLanguage", nextLang);
        if (userData.token) {
          localStorage.setItem("auth_token", userData.token);
        }

        await setLanguage(nextLang, { persist: false });

        resetRecaptcha();

        showSnackbar({
          color: "success",
          title: "Signed in",
          message: "Welcome back!",
          duration: 900,
        });

        setTimeout(() => navigate("/dashboard"), 900);
      } else {
        resetRecaptcha();
        showSnackbar({
          color: "error",
          title: "Login failed",
          message: result.message || "Invalid credentials",
        });
      }
    } catch (err) {
      resetRecaptcha();
      showSnackbar({
        color: "error",
        title: "Connection error",
        message: "Failed to connect to server",
      });
    } finally {
      setLoading(false);
    }
  }

  /* -------------------- LOADING SCREEN -------------------- */
  if (checkingAuth) {
    return (
      <BasicLayout>
        <Card>
          <MDBox
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            mx={2}
            mt={-3}
            p={2}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h4" color="white">
              Sign in to Annotation Portal
            </MDTypography>
          </MDBox>
          <MDBox p={3} textAlign="center">
            <MDTypography>Checking authentication...</MDTypography>
          </MDBox>
        </Card>
      </BasicLayout>
    );
  }

  /* -------------------- UI -------------------- */
  return (
    <BasicLayout>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" color="white">
            Sign in to Annotation Portal
          </MDTypography>
        </MDBox>

        <MDBox p={3}>
          <MDBox component="form">
            <MDBox mb={2} ref={emailRef}>
              <MDInput
                type="email"
                label="Email id"
                fullWidth
                name="email"
                value={email_id}
                onChange={(e) => {
                  setEmailId(e.target.value);
                  if (e.target.value) {
                    setAutoFilled((prev) => ({ ...prev, email: true }));
                  }
                }}
                InputLabelProps={{
                  shrink: Boolean(email_id) || autoFilled.email,
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    // Force label to stay above when autofilled
                    transform: "translate(14px, -9px) scale(0.75)",
                    backgroundColor: "white",
                    padding: "0 4px",
                  },
                  "& .MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              />
            </MDBox>

            <MDBox mb={2} ref={passwordRef}>
              <MDInput
                type="password"
                label="Password"
                fullWidth
                name="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (e.target.value) {
                    setAutoFilled((prev) => ({ ...prev, password: true }));
                  }
                }}
                InputLabelProps={{
                  shrink: Boolean(password) || autoFilled.password,
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    // Force label to stay above when autofilled
                    transform: "translate(14px, -9px) scale(0.75)",
                    backgroundColor: "white",
                    padding: "0 4px",
                  },
                  "& .MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              />
            </MDBox>

            <MDBox mb={2} display="flex" justifyContent="center">
              <ReCaptcha
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                onExpired={() => {
                  setRecaptchaToken("");
                  setRecaptchaError(true);
                }}
                onError={() => {
                  setRecaptchaToken("");
                  setRecaptchaError(true);
                }}
              />
            </MDBox>

            {recaptchaError && (
              <MDBox mb={2}>
                <MDTypography variant="caption" color="error">
                  Please complete the reCAPTCHA verification
                </MDTypography>
              </MDBox>
            )}

            <MDButton variant="gradient" color="info" fullWidth onClick={login} disabled={loading}>
              {loading ? "Signing in..." : "Sign in"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={closeSnackbar}
      >
        <Alert
          severity={snackbar.color}
          variant="filled"
          action={
            <IconButton size="small" onClick={closeSnackbar}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <Typography fontWeight="bold">{snackbar.title}</Typography>
          <Typography variant="body2">{snackbar.message}</Typography>
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
}

export default Basic;
